import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { useGetBatchQuery } from "../../../store/batchApi";
import { useGetStudentInstructorQuery } from "../../../store/studentApi";
import {
  useScheduleInterviewMutation,
  useGetInterviewsByBatchQuery,
} from "@/store/instructor/interview";
import ScheduleInterviewDialog from "./interview/ScheduleInterviewDialog";
import AllScheduledInterviews from "./interview/AllScheduledInterviews";
import {
  Batch,
  InterviewAttempt,
  InterviewBatch,
  InterviewData,
  InterviewRank,
  InterviewStudent,
  RootState,
  ScheduleFormData,
  Student,
  StudentAttempt,
} from "@/types";
import BatchList from "./interview/BatchList";

const InterviewSection: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const { data: allBatches, isLoading: isLoadingBatches } = useGetBatchQuery();
  const { data: allStudents, isLoading: isLoadingStudents } =
    useGetStudentInstructorQuery();
  const [scheduleInterview, { isLoading: isScheduling }] =
    useScheduleInterviewMutation();

  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"students" | "scheduled">(
    "students"
  );

  // FIXED 1: Use stable allBatches instead of derived interviewBatches
  const selectedBatch = useMemo(
    () => allBatches?.find((b: Batch) => b._id === openBatchId),
    [allBatches, openBatchId]
  );

  // FIXED 2: Stable query args — this was the MAIN cause of infinite loop
  const queryArgs = useMemo(
    () => ({
      batchCode: selectedBatch?._id || "",
      Token: user?.token || "",
    }),
    [selectedBatch?._id, user?.token]
  );

  const {
    data: batchInterviews,
    refetch: refetchInterviews,
    isFetching: isFetchingInterviews,
    error: fetchInterviewsError,
  } = useGetInterviewsByBatchQuery(queryArgs, {
    skip: !openBatchId || !user?.token,
  });

  const interviewBatches = useMemo((): InterviewBatch[] => {
    if (!allBatches || !allStudents) return [];

    return allBatches.map((batch: InterviewBatch) => {
      const batchStudents = allStudents.filter(
        (student: Student) =>
          student.batch === batch.bCode && student.status === "Active"
      );

      const studentsWithInterviews: Student[] = batchStudents.map(
        (student: Student) => ({
          _id: student._id,
          name: `${student.fname || ""} ${student.lname || ""}`.trim(),
          email: student.email || "",
          phone: student.contact || "",
          status: "pending" as const,
          ranks: { html_css: 0, javascript: 0, react: 0, backend: 0 },
        })
      );

      return {
        _id: batch._id,
        bCode: batch.bCode,
        bTime: batch.bTime,
        courseName: batch.courseName,
        startTime: batch.startTime,
        endTime: batch.endTime,
        students: studentsWithInterviews,
      };
    });
  }, [allBatches, allStudents]);

  const interviews: InterviewData[] = batchInterviews?.data || [];
  const [allAttempts, setAllAttempts] = useState<
    Record<string, InterviewAttempt[]>
  >({});

  // FIXED 3: Use stable dependency (length instead of full array)
  useEffect(() => {
    if (!user?.token || !openBatchId || interviews.length === 0) {
      setAllAttempts({});
      return;
    }

    const fetchAllAttempts = async () => {
      const attemptsMap: Record<string, InterviewAttempt[]> = {};
      try {
        await Promise.all(
          interviews.map(async (interview) => {
            try {
              const response = await fetch(
                `/api/interview/${interview._id}/attempts`,
                {
                  headers: { Authorization: `Bearer ${user.token}` },
                }
              );
              const data = await response.json();
              attemptsMap[interview._id] = data?.data || [];
            } catch (error) {
              console.log(error);
              attemptsMap[interview._id] = [];
            }
          })
        );
        setAllAttempts(attemptsMap);
      } catch (error) {
        console.error("Error fetching attempts:", error);
      }
    };

    fetchAllAttempts();
  }, [interviews.length, user?.token, openBatchId]); // ← Stable deps only

  // FIXED 4 & 5: Stable memo dependencies
  const batchesWithInterviewData = useMemo(() => {
    if (!selectedBatch) return interviewBatches;

    const studentAttemptsMap: Record<string, StudentAttempt[]> = {};

    Object.entries(allAttempts).forEach(([interviewId, attempts]) => {
      const interview = interviews.find((i) => i._id === interviewId);
      if (!interview) return;

      attempts.forEach((attempt: StudentAttempt) => {
        const studentId = attempt.studentId?._id;

        if (!studentId) return;

        if (!studentAttemptsMap[studentId]) studentAttemptsMap[studentId] = [];

        studentAttemptsMap[studentId].push({
          ...attempt,
          interviewModuleNumber: interview.moduleNumber,
          interviewScheduledDate: interview.scheduledDate,
        });
      });
    });

    return interviewBatches.map((batch) => {
      if (batch._id !== openBatchId) return batch;

      const updatedStudents = batch.students.map((student) => {
        const studentAttempts = studentAttemptsMap[student._id] || [];
        const ranks: InterviewRank = {
          html_css: 0,
          javascript: 0,
          react: 0,
          backend: 0,
        };
        let latestStatus: InterviewStudent["status"] = "pending";
        let latestInterviewDate: Date | undefined;

        studentAttempts.forEach((attempt: StudentAttempt) => {
          const score = attempt.overallScore
            ? Math.round(attempt.overallScore / 10)
            : 0;
          const module = attempt.interviewModuleNumber;
          if (module === 0) ranks.html_css = score;
          else if (module === 1) ranks.javascript = score;
          else if (module === 2) ranks.react = score;
          else if (module === 3) ranks.backend = score;

          const date = attempt.interviewScheduledDate
            ? new Date(attempt.interviewScheduledDate)
            : undefined;
          if (date && (!latestInterviewDate || date > latestInterviewDate)) {
            latestInterviewDate = date;
            latestStatus = attempt.status || "pending";
          }
        });

        return {
          ...student,
          ranks,
          status: latestStatus,
          interviewDate: latestInterviewDate,
        };
      });

      return { ...batch, students: updatedStudents };
    });
  }, [interviewBatches, interviews.length, allAttempts, openBatchId]); // ← Stable!

  const toggleBatch = (batchId: string) => {
    if (openBatchId === batchId) {
      setOpenBatchId(null);
      setActiveTab("students");
    } else {
      setOpenBatchId(batchId);
      setActiveTab("scheduled");
    }
  };

  const openScheduleModal = () => setIsScheduleModalOpen(true);
  const closeScheduleModal = () => setIsScheduleModalOpen(false);

  const handleScheduleSubmit = async (formData: ScheduleFormData) => {
    // ... your existing code (unchanged)
    // (I kept it exactly as you wrote it)
    if (!formData.dateTime || !formData.batchId || !formData.moduleId) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!user?.token) {
      toast.error("User authentication required");
      return;
    }
    const selectedBatch = allBatches?.find(
      (b: Batch) => b._id === formData.batchId
    );
    if (!selectedBatch) {
      toast.error("Batch not found");
      return;
    }
    const moduleMap = [
      { name: "HTML & CSS", number: 0 },
      { name: "JavaScript", number: 1 },
      { name: "Frontend", number: 2 },
      { name: "Backend", number: 3 },
    ];
    const moduleInfo = moduleMap[parseInt(formData.moduleId)];
    if (!moduleInfo) {
      toast.error("Invalid module selected");
      return;
    }
    const apiData = {
      batchCode: selectedBatch._id,
      moduleName: moduleInfo.name,
      moduleNumber: moduleInfo.number,
      scheduledDate: formData.dateTime,
      conductedBy: user?._id,
    };

    try {
      await scheduleInterview({
        Token: user.token,
        formData: apiData,
      }).unwrap();
      toast.success(
        `Interview "${moduleInfo.name}" scheduled successfully for ${selectedBatch.bCode}!`
      );
      closeScheduleModal();
      if (openBatchId === formData.batchId && refetchInterviews) {
        setTimeout(() => refetchInterviews(), 500);
      }
    } catch (error) {
      console.error("Error scheduling interview:", error);
      toast.error(
        error?.data?.message || error?.message || "Failed to schedule interview"
      );
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl font-bold">
              Interview Dashboard
            </CardTitle>
            <Button
              onClick={openScheduleModal}
              className="bg-[#26BDB0] hover:bg-[#49ccc1e2]
 text-white"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Interview
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          if (v === "students" || v === "scheduled") {
            setActiveTab(v);
          }
        }}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="students">Students & Rankings</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Interviews</TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              {(isLoadingBatches || isLoadingStudents) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    Loading batches and students...
                  </p>
                </div>
              )}
              {!isLoadingBatches && !isLoadingStudents && (
                <BatchList
                  batches={batchesWithInterviewData}
                  openBatchId={openBatchId}
                  onToggleBatch={toggleBatch}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-4">
          {openBatchId ? (
            <AllScheduledInterviews
              interviews={interviews}
              batchName={selectedBatch?.bCode}
              isLoading={isFetchingInterviews}
              error={fetchInterviewsError}
              batchInfo={{
                bCode: selectedBatch?.bCode,
                _id: selectedBatch?._id,
              }}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>
                    Select a batch from the Students tab to view scheduled
                    interviews,
                  </p>
                  <p className="text-sm mt-2">
                    or schedule a new interview to see it here.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <ScheduleInterviewDialog
        isOpen={isScheduleModalOpen}
        onClose={closeScheduleModal}
        onSubmit={handleScheduleSubmit}
        batches={allBatches || []}
        isLoading={isScheduling}
      />
    </div>
  );
};

export default InterviewSection;
